import type { ReactNode } from "react";
import { Trans } from "@lingui/react/macro";
import { Button } from "@app/ui";

/**
 * 렌더 크래시 시 흰 화면 대신 보여주는 공용 폴백 UI. 최상위
 * components/ErrorBoundary.tsx(라우터 진입 전 화면)와 Router.tsx 의
 * defaultErrorComponent(라우트 내부 크래시) 양쪽에서 같은 디자인을 쓰도록 추출했다.
 *
 * 복구는 상태 리셋이 아니라 하드 리로드로 한다 — 렌더가 깨진 원인(오래된 청크·
 * 깨진 캐시)이 그대로면 되돌려도 다시 터지기 때문이다. TanStack Router 의
 * errorComponent(RouteComponent) 로도 쓰이므로 { error, reset, info } props 가
 * 넘어오지만, 폴백은 일반 문구만 보여주면 되므로 무시한다.
 *
 * 컨테이너는 fixed 전체 뷰포트 오버레이다 — Router.tsx 의 defaultErrorComponent 로
 * 라우트 내부 크래시를 잡으면 폴백이 Outlet 자리(사이드바 옆 콘텐츠 페인) 안에
 * 렌더되는데, 그래도 사이드바까지 화면 전체를 덮어야 하기 때문이다. 데스크톱
 * 셸의 상단 드래그 영역(DesktopTitleBar, z-50)만 top 오프셋으로 비운다(웹/모바일은
 * --app-titlebar-height 가 0이라 사실상 inset-0 전체를 덮는다).
 */
export function ErrorFallback(): ReactNode {
  return (
    <div className="fixed inset-x-0 bottom-0 top-[var(--app-titlebar-height)] z-40 flex flex-col items-center justify-center gap-6 bg-background px-6 text-center">
      <div className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <AlertTriangleIcon className="size-7" />
      </div>
      <div className="flex max-w-xs flex-col gap-1.5">
        <h1 className="text-base font-semibold text-foreground">
          <Trans>문제가 생겨 화면을 표시할 수 없어요</Trans>
        </h1>
        <p className="text-sm text-muted-foreground">
          <Trans>새로고침하면 대부분 해결돼요. 계속 문제가 있으면 잠시 후 다시 시도해 주세요.</Trans>
        </p>
      </div>
      <Button onClick={() => window.location.reload()}>
        <RefreshIcon />
        <Trans>새로고침</Trans>
      </Button>
    </div>
  );
}

// apps/app 은 lucide-react 를 직접 의존하지 않으므로(모두 @app/ui 경유),
// 폴백 화면에 필요한 두 아이콘만 currentColor 인라인 SVG 로 둔다.
// 크기는 className 으로 지정(Button 안에서는 base 의 [&_svg]:size-4 가 적용됨).
function AlertTriangleIcon({ className }: { className?: string }): ReactNode {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function RefreshIcon(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}
